/* eslint-disable @atlassian/relay/unused-fields */
/* eslint-disable @atlassian/relay/graphql-naming */
/* eslint-disable @atlassian/relay/no-wrapping-hooks */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	graphql,
	usePreloadedQuery,
	useRefetchableFragment,
	usePaginationFragment,
	type PreloadedQuery,
	type Disposable,
} from 'react-relay';
import type { User } from '@jira/platform__rest-user/src/user.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import type { DataBasic } from '@jira/platform__services-user-current/src/types.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';

import type {
	ObjectListFlyoutHookParams,
	ObjectListFlyoutHookResults,
} from '@atlassian/navigation-common/types';

import { useIntl } from '@atlassian/jira-intl';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { getSections } from './getSections.tsx';
import type { JiraRecentOLFItem } from './JiraRecentOLFItem.types.tsx';
import type { useRecentItemsPaged$key } from './__generated__/useRecentItemsPaged.graphql.ts';
import type { useRecentItemsPaginationQuery } from './__generated__/useRecentItemsPaginationQuery.graphql.ts';
import type { useRecentItemsRefetchableQuery } from './__generated__/useRecentItemsRefetchableQuery.graphql.ts';
import type { useRecentItemsQuery } from './__generated__/useRecentItemsQuery.graphql.ts';
import type { useRecentItems$key } from './__generated__/useRecentItems.graphql.ts';

const DEBOUNCE_INTERVAL = 300;

type PreloadedQueries = {
	recentMenuData: PreloadedQuery<useRecentItemsQuery>;
};

export function useRecentItems(
	params: ObjectListFlyoutHookParams<PreloadedQueries>,
): ObjectListFlyoutHookResults<JiraRecentOLFItem> {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const { searchText, queries } = params as { searchText: string; queries: PreloadedQueries };
	const { formatMessage } = useIntl();
	const userLocale = useLocale();
	const recentMenuData = queries.recentMenuData;
	const { cloudId, count } = recentMenuData.variables;

	const queryData = usePreloadedQuery<useRecentItemsQuery>(
		graphql`
			query useRecentItemsQuery($cloudId: ID!, $keyword: String = "", $count: Int!) @preloadable {
				jira @required(action: THROW) {
					recent: recentItems(
						cloudId: $cloudId
						types: [PROJECT, FILTER, DASHBOARD, BOARD, QUEUE, ISSUE, PLAN, JPD_COLLECTION]
						first: 1
						filter: { keyword: $keyword }
					) @required(action: THROW) {
						pageInfo @required(action: THROW) {
							startCursor
						}
					}
				}
				...useRecentItems @arguments(count: $count)
			}
		`,
		recentMenuData,
	);

	const [fragmentData, refetch] = useRefetchableFragment<
		useRecentItemsRefetchableQuery,
		useRecentItems$key
	>(
		graphql`
			fragment useRecentItems on Query
			@refetchable(queryName: "useRecentItemsRefetchableQuery")
			@argumentDefinitions(count: { type: "Int!" }) {
				...useRecentItemsPaged @arguments(count: $count)
			}
		`,
		queryData,
	);

	// NOTE: refetch() returns a Disposable resource. This resource should be dispose()'d of
	// before subsequent calls to refetch.
	// We also want to debounce changes-to-the-search-string.
	const disposableSearchRef = useRef<Disposable | null>(null);

	const handleFilterChange = useCallback(
		(searchQuery: string) => refetch({ keyword: searchQuery, cloudId }),
		[cloudId, refetch],
	);

	const [handleDebouncedSearchChange] = useDebouncedCallback<(newSearchQuery?: string) => void>(
		(newSearchQuery = searchText) => {
			disposableSearchRef.current?.dispose();
			disposableSearchRef.current = handleFilterChange(newSearchQuery ?? '');
		},
		DEBOUNCE_INTERVAL,
	);

	useEffect(() => {
		handleDebouncedSearchChange(searchText ?? '');
	}, [handleDebouncedSearchChange, searchText]);

	const {
		data: { user },
		// oxlint-disable-next-line typescript/consistent-type-assertions
	} = useCurrentUser() as { data: { user: DataBasic & User } };
	const timeZone = typeof user.timeZone === 'string' ? user.timeZone : '';

	const { data, /* isLoadingNext, */ hasNext, loadNext } = usePaginationFragment<
		useRecentItemsPaginationQuery,
		useRecentItemsPaged$key
	>(
		graphql`
			fragment useRecentItemsPaged on Query
			@refetchable(queryName: "useRecentItemsPaginationQuery")
			@argumentDefinitions(
				cursor: { type: "String" }
				count: { type: "Int" }
				isJpdRoadmapsRecentsEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/isJpdRoadmapsRecentsEnabled"
				}
				isGsnBoardUrlFixEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/isGsnBoardUrlFixEnabled"
				}
			) {
				jira @required(action: THROW) {
					_recentItems: recentItems(
						cloudId: $cloudId
						types: [PROJECT, FILTER, DASHBOARD, BOARD, QUEUE, ISSUE, PLAN, JPD_COLLECTION]
						first: $count
						filter: { keyword: $keyword }
						after: $cursor
					) @connection(key: "useRecentItemsPaged__recentItems") {
						edges {
							node {
								__typename
								... on JiraIssue {
									id
									lastViewedTimestamp
									# JiraRecentMenuIssueItem
									summaryField {
										text
									}
									issueWebUrl: webUrl
									issueId
								}
								... on JiraProject {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuProjectItem
									title: name
									key
									defaultNavigationItem {
										... on JiraNavigationItem {
											url
										}
									}
									description
									avatar {
										large
									}
									projectId
								}
								... on JiraPlan {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuPlanItem
									planId
									scenario {
										scenarioId
									}
									planTitle: title
								}
								... on JiraBoard {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuBoardItem
									name
									boardUrl @skip(if: $isGsnBoardUrlFixEnabled)
									boardId
									defaultNavigationItem @include(if: $isGsnBoardUrlFixEnabled) {
										... on JiraNavigationItem {
											url
										}
									}
								}
								... on JiraDashboard {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuDashboardItem
									dashboardTitle: title
									dashboardUrl
									dashboardId
								}
								... on JiraSystemFilter {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuFilterItem
									filterName: name
									filterUrl
									filterId
								}
								... on JiraCustomFilter {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuFilterItem
									filterName: name
									filterUrl
									filterId
								}
								... on JiraServiceManagementQueue {
									id
									lastViewedTimestamp
									# ...JSMRecentMenuQueueItem
									queueTitle: title
									queueUrl
									queueId
								}
								... on JiraProductDiscoveryCollection @include(if: $isJpdRoadmapsRecentsEnabled) {
									id
									lastViewedTimestamp
									collectionTitle: title
									collectionUrl
									collectionId
									emoji
								}
							}
						}
						pageInfo {
							hasNextPage
							endCursor
						}
					}
				}
			}
		`,
		fragmentData,
	);

	const accountId = user.accountId;

	const deps = useMemo(
		() => ({ formatMessage, locale: userLocale, cloudId, accountId }),
		[formatMessage, userLocale, cloudId, accountId],
	);

	const { sections } = useMemo(() => {
		const nodes = data.jira._recentItems?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
		return {
			sections: getSections(nodes, timeZone, deps),
		};
	}, [data.jira._recentItems?.edges, timeZone, deps]);

	const loadMore = useCallback(() => loadNext(count), [loadNext, count]);

	if (searchText === '') {
		return { sections, hasMore: hasNext, isLoading: false, loadMore };
	}

	return {
		hasMore: hasNext,
		isLoading: false,
		loadMore,
		sections,
	};
}
